import { DetailedRequisitionSerializer } from "../../../recoil/types";
import { fetchOrLogout } from "../../../utils/helpers";
import { getBaseUrl } from "../../../utils/requestUtils";

export interface SpecificRequisitionBilling {
  employer: string;
  icd_1: string;
  icd_2: string;
  icd_3: string;
  primary_insurance: string;
  primary_insurance_group: string;
  primary_insurance_id: string;
  relationship_to_insured: string;
  remaining_icd_codes: string;
  street_address_1: string;
  city: string;
  state: string;
  zip: string;
  subscriber_date_of_birth: string;
  subscriber_name: string;
}

export interface UpdateSpecificRequsitionRequestType extends SpecificRequisitionBilling {
  due_date: string;
  // SWE-4918: set by the "Pending Medicaid" checkbox on the Missing Billing
  // Info form. When true, the backend fills in primary_insurance, _id, and
  // _group with the values clinic staff have been typing manually today
  // ("Pending Medicaid", "0", "0") and tags the internal Billing alert email
  // so Billing knows to follow up on the patient's Medicaid card.
  is_pending_medicaid?: boolean;
}

export const updateSpecificRequsitionInitialValues: UpdateSpecificRequsitionRequestType =
  {
    employer: "",
    icd_1: "",
    icd_2: "",
    icd_3: "",
    primary_insurance: "",
    primary_insurance_group: "",
    primary_insurance_id: "",
    relationship_to_insured: "",
    remaining_icd_codes: "",
    street_address_1: "",
    city: "",
    state: "",
    zip: "",
    subscriber_date_of_birth: "",
    subscriber_name: "",
    due_date: "",
    is_pending_medicaid: false,
  };

export const PatchSpecificRequisition = async (
  token: string,
  sfid: string,
  updates: UpdateSpecificRequsitionRequestType,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response> => {
  const baseUrl = await getBaseUrl();
  return fetchOrLogout(
    `${baseUrl}/api/v1/provider_portal/requisitions/${sfid}/`,
    {
      method: "PATCH",
      body: JSON.stringify(updates),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    setLoggedOut,
  );
};

export const GetInsuranceList = async (
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response> => {
  const baseUrl = await getBaseUrl();
  return fetchOrLogout(
    `${baseUrl}/api/v1/provider_portal/insurance`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    setLoggedOut,
  );
};

export const uploadImageForProcessing = async (
  file_data: string,
  sfid: string,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<{ report?: DetailedRequisitionSerializer; valid: boolean }> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/file_upload`,
      {
        method: "POST",
        body: JSON.stringify({ file_data, sfid }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );
    if (response.ok) {
      const responseData = await response.json();
      return {
        report: responseData as DetailedRequisitionSerializer,
        valid: true,
      };
    }
    if (response.status === 400) {
      return { report: undefined, valid: false };
    }
    return { report: undefined, valid: false };
  } catch (e) {
    return { report: undefined, valid: false };
  }
};

export const toBase64 = (file: File): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    (reader as FileReader).readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export const uploadFinalImages = async (
  file_names: string[],
  sfid: string,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<DetailedRequisitionSerializer | undefined> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/file_upload_submit`,
      {
        method: "POST",
        body: JSON.stringify({ file_names, sfid }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );
    if (response.ok) {
      const newReport = await response.json();
      return newReport as DetailedRequisitionSerializer;
    }
    return undefined;
  } catch (e) {
    return undefined;
  }
};

export interface GenerateTreatmentNotesResponse extends DetailedRequisitionSerializer {
  generated_notes_download_url: string;
}

export const generateTreatmentNotes = async (
  treatment_notes: string,
  sfid: string,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<GenerateTreatmentNotesResponse | undefined> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/custom_treatment_notes`,
      {
        method: "POST",
        body: JSON.stringify({ treatment_notes, sfid }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );
    if (response.ok) {
      const newReport = await response.json();
      return newReport as GenerateTreatmentNotesResponse;
    }
    return undefined;
  } catch (e) {
    return undefined;
  }
};

export const generateProviderLetter = async (
  body_text: string,
  name: string,
  willing_to_speak_with_legal: boolean,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<GenerateTreatmentNotesResponse | undefined> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/physician_letter`,
      {
        method: "POST",
        body: JSON.stringify({
          body_text,
          name,
          willing_to_speak_with_legal,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );
    if (response.ok) {
      const newReport = await response.json();
      return newReport as GenerateTreatmentNotesResponse;
    }
    return undefined;
  } catch (e) {
    return undefined;
  }
};

export const GetTreatmentNotesDownloadURL = async (
  token: string,
  sfid: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response> => {
  const baseUrl = await getBaseUrl();
  return fetchOrLogout(
    `${baseUrl}/api/v1/provider_portal/custom_treatment_notes?sfid=${sfid}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    setLoggedOut,
  );
};

export const setUpdatedInternalNotes = async (
  internal_notes: string,
  sfid: string,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<DetailedRequisitionSerializer | undefined> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/internal_notes/${sfid}/`,
      {
        method: "PATCH",
        body: JSON.stringify({ internal_notes }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );
    if (response.ok) {
      const newReport = await response.json();
      return newReport as DetailedRequisitionSerializer;
    }
    return undefined;
  } catch (e) {
    return undefined;
  }
};

export const userReportIntlBabyPeek = async (
  req_identifier: string,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<DetailedRequisitionSerializer | undefined> => {
  try {
    const baseUrl = await getBaseUrl();
    const response = await fetchOrLogout(
      `${baseUrl}/api/v1/provider_portal/intl_baby_peek`,
      {
        method: "POST",
        body: JSON.stringify({ req_identifier }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
      setLoggedOut,
    );
    if (response.ok) {
      const newReport = await response.json();
      return newReport as DetailedRequisitionSerializer;
    }
    return undefined;
  } catch (e) {
    return undefined;
  }
};

export const shareIntlBabyPeekPdf = async (
  req_identifier: string,
  email: string,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response> => {
  const baseUrl = await getBaseUrl();
  return fetchOrLogout(
    `${baseUrl}/api/v1/provider_portal/intl_baby_peek/share`,
    {
      method: "POST",
      body: JSON.stringify({ req_identifier, email }),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    setLoggedOut,
  );
};

export const downloadIntlBabyPeekPdf = async (
  req_identifier: string,
  token: string,
  setLoggedOut: (inactivity: boolean) => void,
): Promise<Response> => {
  const baseUrl = await getBaseUrl();
  return fetchOrLogout(
    `${baseUrl}/api/v1/provider_portal/intl_baby_peek/download`,
    {
      method: "POST",
      body: JSON.stringify({ req_identifier }),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    setLoggedOut,
  );
};
